import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { AccountsVendor } from '../types';

export function useAccountsCustomers() {
  return useQuery({
    queryKey: ['accounts', 'customers'],
    queryFn: async () => {
      const [{ data: legacyCustomers }, accPartiesRes] = await Promise.all([
        api.get<any[]>('/api/accounts/parties/customers').catch(() => ({ data: [] })),
        api.get<any>('/api/accounting/parties?role=customer').catch(() => ({ data: [] })),
      ]);

      const accParties = Array.isArray(accPartiesRes?.data?.data)
        ? accPartiesRes.data.data
        : (Array.isArray(accPartiesRes?.data) ? accPartiesRes.data : []);

      const map = new Map<string, any>();

      (legacyCustomers || []).forEach((c: any) => {
        const id = String(c._id || c.id);
        if (id) {
          map.set(id, {
            ...c,
            name: c.name || c.displayName || c.legalName || 'Unnamed Customer',
            displayName: c.displayName || c.name || c.legalName,
            legalName: c.legalName || c.name || c.displayName,
          });
        }
      });

      (accParties || []).forEach((p: any) => {
        const id = String(p._id || p.id);
        if (id && !map.has(id)) {
          map.set(id, {
            _id: p._id,
            id: p._id,
            name: p.displayName || p.legalName || p.name || 'Unnamed Customer',
            displayName: p.displayName || p.legalName,
            legalName: p.legalName || p.displayName,
            email: p.email || '',
            phone: p.mobile || p.phone || '',
            gstNumber: p.taxNumber || p.gstNumber || '',
            contactId: p.contactId || '',
          });
        }
      });

      return Array.from(map.values());
    },
  });
}

export const useParties = useAccountsCustomers;

export function useCreateAccountsCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/parties/customers', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'customers'] }),
  });
}

export function useAccountsVendors() {
  return useQuery({
    queryKey: ['accounts', 'vendors'],
    queryFn: async () => {
      const { data } = await api.get<AccountsVendor[]>('/api/accounts/parties/vendors');
      return data;
    },
  });
}

export function useCreateAccountsVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/parties/vendors', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts', 'vendors'] }),
  });
}

export function useCustomerLedger(customerId: string, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'customers', customerId, 'ledger', startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get(`/api/accounts/parties/customers/${customerId}/ledger?${params.toString()}`);
      return data;
    },
    enabled: !!customerId,
  });
}

export function useVendorLedger(vendorId: string, startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['accounts', 'vendors', vendorId, 'ledger', startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const { data } = await api.get(`/api/accounts/parties/vendors/${vendorId}/ledger?${params.toString()}`);
      return data;
    },
    enabled: !!vendorId,
  });
}
